/**
 * UnifiedInboxMessageModal
 *
 * Modal component to display the full content of a unified inbox message.
 * Supports both platform messages and application messages.
 */

import { useNavigate } from 'react-router-dom';
import {
  TicketIcon,
  ChatBubbleLeftIcon,
  ArrowPathIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  EnvelopeIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import type { UnifiedInboxItem } from '../../../types/app-message.types';
import { messagePriorityColors } from '../../../types/app-message.types';
import { SourceBadge } from './SourceBadge';

interface UnifiedInboxMessageModalProps {
  item: UnifiedInboxItem | null;
  isOpen: boolean;
  onClose: () => void;
  onArchive?: (item: UnifiedInboxItem) => void;
  onDelete?: (item: UnifiedInboxItem) => void;
}

// Icon map for platform message types
const typeIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  TICKET_CREATED: TicketIcon,
  TICKET_REPLIED: ChatBubbleLeftIcon,
  TICKET_STATUS_CHANGED: ArrowPathIcon,
  TICKET_ASSIGNED: UserPlusIcon,
  SLA_BREACH: ExclamationTriangleIcon,
  ANNOUNCEMENT: MegaphoneIcon,
  DIRECT_MESSAGE: EnvelopeIcon,
};

// Color map for platform message types
const typeColorMap: Record<string, string> = {
  TICKET_CREATED: 'bg-blue-100 text-blue-600',
  TICKET_REPLIED: 'bg-green-100 text-green-600',
  TICKET_STATUS_CHANGED: 'bg-yellow-100 text-yellow-600',
  TICKET_ASSIGNED: 'bg-purple-100 text-purple-600',
  SLA_BREACH: 'bg-red-100 text-red-600',
  ANNOUNCEMENT: 'bg-orange-100 text-orange-600',
  DIRECT_MESSAGE: 'bg-gray-100 text-gray-600',
};

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Simple markdown-to-HTML renderer
 * Supports basic markdown features without external dependencies
 */
function renderMarkdown(text: string): string {
  let html = text;

  // Escape HTML to prevent XSS
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Code: `code`
  html = html.replace(/`(.+?)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded text-sm">$1</code>');

  // Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

  // Line breaks
  html = html.replace(/\n/g, '<br>');

  return html;
}

export function UnifiedInboxMessageModal({
  item,
  isOpen,
  onClose,
  onArchive,
  onDelete,
}: UnifiedInboxMessageModalProps) {
  const navigate = useNavigate();

  if (!item) return null;

  // For app messages, use a generic icon
  // For platform messages, use the type-specific icon
  const Icon = item.source === 'application'
    ? EnvelopeIcon
    : (typeIconMap[item.sender.type] || EnvelopeIcon);

  const iconColor = item.source === 'application'
    ? 'bg-blue-100 text-blue-600'
    : (typeColorMap[item.sender.type] || 'bg-gray-100 text-gray-600');

  const priorityColor = messagePriorityColors[item.priority] || 'bg-gray-100 text-gray-700';

  const handleAction = () => {
    if (item.actionUrl) {
      // If it's an internal URL, use navigate
      if (item.actionUrl.startsWith('/')) {
        navigate(item.actionUrl);
        onClose();
      } else {
        // External URL, open in new tab
        window.open(item.actionUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleArchive = () => {
    if (onArchive) {
      onArchive(item);
      onClose();
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(item);
      onClose();
    }
  };

  // Render body based on source
  const renderedBody = item.source === 'application'
    ? renderMarkdown(item.body)
    : item.body;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <div className="flex items-start gap-4 pr-8">
          <div className={clsx('flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center', iconColor)}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 break-words">{item.subject}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', priorityColor)}>
                {item.priority}
              </span>
              <SourceBadge
                source={item.source}
                applicationName={item.sender.name}
                applicationCode={item.sender.applicationCode}
              />
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Sender and time info */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">From:</span>
            <span>{item.sender.name}</span>
            <span className="text-xs text-gray-400">
              ({item.source === 'platform' ? 'system' : 'application'})
            </span>
          </div>
          <time dateTime={item.createdAt}>{formatDateTime(item.createdAt)}</time>
        </div>

        {/* Message body */}
        <div className="prose prose-sm max-w-none">
          {item.source === 'application' ? (
            <div
              className="text-gray-700"
              dangerouslySetInnerHTML={{ __html: renderedBody }}
            />
          ) : (
            <p className="text-gray-700 whitespace-pre-wrap">{item.body}</p>
          )}
        </div>

        {/* Action button for app messages with actionUrl */}
        {item.source === 'application' && item.actionUrl && (
          <div className="mt-6">
            <Button variant="primary" onClick={handleAction} className="w-full sm:w-auto">
              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
              View Details
            </Button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {onArchive && !item.isArchived && !item.isDeleted && (
          <Button variant="outline" onClick={handleArchive}>
            Archive
          </Button>
        )}
        {onDelete && !item.isDeleted && (
          <Button variant="outline" onClick={handleDelete} className="text-red-600 border-red-300 hover:bg-red-50">
            Delete
          </Button>
        )}
        {item.actionUrl && item.source === 'platform' && (
          <Button variant="primary" onClick={handleAction}>
            View
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default UnifiedInboxMessageModal;
